import { useApi } from '@classgo/app-kit';
import { Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { EntityList } from '../../components/entity-list';
import { GroupForm } from '../../forms/group-form';
import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * The groups of the selected sede in a school year (the active one by default).
 */
export const GroupsScreen = () => {
  const t = useT();
  const api = useApi();
  const { session, sede } = useTenant();
  const years = useQuery(listQuery(api, 'years'));
  const levels = useQuery(listQuery(api, 'levels'));
  const grades = useQuery(listQuery(api, 'grades'));
  const [yearId, setYearId] = useState('');
  const yearItems = years.data?.items ?? [];
  useEffect(() => {
    if (!yearId && yearItems.length > 0) {
      setYearId(yearItems.find((year) => year.status === 'active')?.id ?? yearItems[0]?.id ?? '');
    }
  }, [yearId, yearItems]);
  const gradeName = (id: string) => grades.data?.items.find((grade) => grade.id === id)?.name ?? '';

  return (
    <EntityList
      entity="groups"
      query={{ sedeId: sede.id, schoolYearId: yearId }}
      title={t('groups.title')}
      caption={t('groups.caption')}
      createLabel={t('groups.new')}
      editTitle={t('groups.edit')}
      emptyTitle={t('groups.empty.title')}
      emptyBody={yearItems.length === 0 ? t('groups.noYear') : t('groups.empty.body')}
      toolbar={
        <>
          <Select
            label={t('groups.filter.year')}
            value={yearId}
            onChange={(event) => setYearId(event.target.value)}
            options={yearItems.map((year) => ({ value: year.id, label: year.name }))}
            placeholder={yearItems.length === 0 ? t('groups.noYear') : undefined}
          />
          <p className="self-end pb-2 text-sm text-gray-500">
            {t('groups.sedeNote', { sede: sede.name })}
          </p>
        </>
      }
      columns={[
        {
          key: 'name',
          header: t('groups.column.name'),
          cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
        },
        { key: 'grade', header: t('groups.column.grade'), cell: (row) => gradeName(row.gradeId) },
        {
          key: 'jornada',
          header: t('groups.column.jornada'),
          cell: (row) => t(`groups.jornada.${row.jornada}`),
        },
      ]}
      renderForm={({ current, onDone }) => (
        <GroupForm
          years={yearItems}
          sedes={session.sedes}
          levels={levels.data?.items ?? []}
          grades={grades.data?.items ?? []}
          schoolYearId={yearId}
          sedeId={sede.id}
          current={current}
          onDone={onDone}
        />
      )}
    />
  );
};
